import { Link } from "expo-router";
import type { ComponentType, ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import type { ColorRole } from "@/theme/tokens";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { Button } from "@/ui/Button";
import type { IconProps } from "@/ui/icons/LineIcon";
import { Reveal } from "@/ui/motion";
import { Text } from "@/ui/Text";

import { AppBar } from "./chrome/AppBar";
import { useChromeKind } from "./chrome/useChromeKind";
import { useScreenStyles } from "./screenStyles";

type Props = {
  title: string;
  /** testID, e.g. `placeholder-home`. */
  testID: string;
  /** 006 (FR-004): replaces the default "Coming soon". */
  message?: string;
  /** The section's own icon, white on a colour tile above the title. */
  Icon?: ComponentType<IconProps>;
  /** The tile's colour and its ink (default: Akaiva blue, white). */
  tile?: ColorRole;
  ink?: ColorRole;
  /** Shows "Go to Home" under the message (parent sections; desktop only, phones have the tabs). */
  homeLink?: boolean;
  /** A screen pushed over the tabs (Cart): phones show the app bar with the title. */
  pushed?: boolean;
  children?: ReactNode;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    disc: {
      width: 112,
      height: 112,
      borderRadius: t.radius.card,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: t.space.sm,
    },
    action: { marginTop: t.space.sm, minWidth: 200 },
  }),
);

/** A section placeholder until its feature lands (FR-003): an icon, the title and "Coming soon". */
export function PlaceholderScreen({
  title,
  testID,
  message = "Coming soon",
  Icon,
  tile = "tileBlue",
  ink = "onTile",
  homeLink = false,
  pushed = false,
  children,
}: Props) {
  const styles = useScreenStyles();
  const own = useStyles();
  const theme = useTheme();
  const phone = useChromeKind() === "mobile";
  const appBar = phone && pushed;
  return (
    <View style={styles.screen} testID={testID}>
      {appBar ? <AppBar title={title} /> : null}
      <View style={styles.body} role="main">
        {Icon ? (
          <Reveal>
            <View style={[own.disc, { backgroundColor: theme.color[tile] }]}>
              <Icon color={theme.color[ink]} size={theme.size.icon.xl} />
            </View>
          </Reveal>
        ) : null}
        {appBar ? null : (
          <Text variant="display" accessibilityRole="header" aria-level={1}>
            {title}
          </Text>
        )}
        <Text tone="muted" style={styles.message}>
          {message}
        </Text>
        {homeLink && !phone ? (
          <View style={own.action}>
            <Link href="/" asChild>
              <Button variant="outline" accessibilityRole="link">
                Go to Home
              </Button>
            </Link>
          </View>
        ) : null}
        {children}
      </View>
    </View>
  );
}
